import { EntitlementSlugEnum } from '@electricjs/core_entity-client';
import {
  Flex,
  Icon,
  Link,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalV2,
  Text,
} from '@electricjs/arc';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import formatDate from '@/helpers/formatDate';
import StyledRouterLink from '@/components/StyledRouterLink';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';

const BreachDescriptionModal = ({
  breachName,
  breachDate,
  breachDomain,
  closeModal,
  markResolved,
}: {
  breachName: string;
  breachDate: string;
  breachDomain: string;
  closeModal: () => void;
  markResolved: () => void;
}) => {
  const { t } = useTranslation('pages');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasMarketplaceEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Marketplace
  );

  return (
    <ModalV2
      minWidth="40rem"
      maxWidth="70rem"
      visible
      hide={closeModal}
      ariaLabelledby="gigawatt-breach-description-modal">
      <ModalHeader>
        <Text variant="heading-2">
          {breachName} {t('passwordBreaches.breachModal.title')}
        </Text>
      </ModalHeader>
      <ModalBody>
        <Flex flexDirection="column">
          <Text>
            <b>{t('passwordBreaches.breachModal.breachName')}</b> {breachName}
          </Text>
          <Text>
            <b>{t('passwordBreaches.breachModal.breachDate')}</b>{' '}
            {formatDate(breachDate, {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })}
          </Text>
          <Text>
            <b>{t('passwordBreaches.breachModal.websiteDomain')}</b>{' '}
            <Link
              href={`https://${breachDomain}`}
              target="_blank"
              rel="noreferrer noopener">
              <Flex display="inline-flex">
                {breachDomain}
                <Icon display="inline" icon="external-link" ml="0.5rem" />
              </Flex>
            </Link>
          </Text>
        </Flex>

        <Text my="1.6rem">
          <Trans
            i18nKey="passwordBreaches.breachModal.fullBestPractices"
            ns="pages"
            components={{
              br: <br />,
              spacer: <span style={{ display: 'block', height: '1.6rem' }} />,
              StyledRouterLink: hasMarketplaceEntitlement ? (
                <StyledRouterLink
                  id="breach-keeper-modal-link"
                  to="/discover?addon=keeper"
                  onClick={closeModal}>
                  <span style={{ color: 'inherit' }} />
                </StyledRouterLink>
              ) : (
                // No Marketplace entitlement -> /discover route doesn't exist, so keep the
                // sentence but drop the link to avoid a 404.
                <span />
              ),
            }}
          />
        </Text>
      </ModalBody>
      <ModalFooter>
        <Flex width="100%" justifyContent="space-between" alignItems="center">
          {hasMarketplaceEntitlement ? (
            <StyledRouterLink
              id="breach-learn-more-security-link"
              to="/discover"
              onClick={closeModal}>
              <Text variant="legal" color="inherit">
                {t('passwordBreaches.breachModal.learnMore')}
              </Text>
            </StyledRouterLink>
          ) : (
            // No Marketplace entitlement -> /discover route doesn't exist, hide the link.
            // Empty span keeps the footer's space-between layout unchanged.
            <span />
          )}
          <Flex>
            <ModalAction
              id="gigawatt-breach-description-modal-close"
              onClick={closeModal}
              mr="2.6rem"
              variant="text">
              {t('passwordBreaches.breachModal.cancel')}
            </ModalAction>
            <ModalAction
              id="gigawatt-breach-description-modal-resolve"
              loading={isSubmitting}
              onClick={() => {
                setIsSubmitting(true);
                // Modal is automatically closed by this function
                markResolved();
              }}>
              {t('passwordBreaches.breachModal.markResolved')}
            </ModalAction>
          </Flex>
        </Flex>
      </ModalFooter>
    </ModalV2>
  );
};

export default BreachDescriptionModal;
